// ============================================================
// FILE: src/hooks/useActivityTracker.js
// — Detects user activity (click, keypress, mousemove, scroll)
// — Auto-sets offline after 5 minutes ng walang action
// ============================================================
import { useEffect, useRef, useCallback } from "react";
import config from "../config";

const INACTIVE_TIMEOUT = 5 * 60 * 1000; // 5 minutes

const useActivityTracker = () => {
  const inactivityTimer = useRef(null);

  const getUsername = () => {
    try {
      const adminRaw = localStorage.getItem("admin");
      if (adminRaw) {
        const admin = JSON.parse(adminRaw);
        if (admin?.username && admin.username !== "superadmin") {
          return admin.username;
        }
      }
    } catch { /* ignore */ }
    return null;
  };

  // ── Set admin offline ─────────────────────────────────────
  const setOffline = useCallback(async () => {
    const username = getUsername();
    if (!username) return;

    try {
      const formData = new URLSearchParams();
      formData.append("username", username);
      await fetch(`${config.API_BASE_URL}/Admin/logout`, {
        method: "POST",
        body: formData,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      console.log("💤 Admin set offline due to inactivity.");
    } catch (err) {
      console.warn("⚠️ Set offline failed:", err);
    }
  }, []);

  // ── Reset inactivity timer ────────────────────────────────
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);

    inactivityTimer.current = setTimeout(() => {
      setOffline();
    }, INACTIVE_TIMEOUT);
  }, [setOffline]);

  // ── Handle any user activity ──────────────────────────────
  const handleActivity = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  useEffect(() => {
    const username = getUsername();
    if (!username) return;

    // ── Listen sa user activity events ───────────────────────
    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
    events.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));

    // ── Start inactivity timer agad ──────────────────────────
    resetInactivityTimer();

    return () => {
      events.forEach(e => window.removeEventListener(e, handleActivity));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [handleActivity, resetInactivityTimer]);
};

export default useActivityTracker;